import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

@customElement('checkbox-field')
export class CheckboxField extends LitElement {
  @property({ type: String }) name = '';
  @property({ type: String }) label = '';
  @property({ type: Boolean }) value = false;
  @property({ type: Boolean }) required = false;
  @property({ type: String }) error = '';
  @property({ type: Boolean }) touched = false;

  static styles = css`
    :host {
      display: block;
    }
    .field-group {
      margin-bottom: 0;
    }
    .checkbox-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 14px 16px;
      border-radius: 10px;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      background: #ffffff;
      border: 1.5px solid #e5e7eb;
      box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
      isolation: isolate;
    }

    .checkbox-wrapper::before {
      content: '';
      position: absolute;
      inset: -1px;
      border-radius: 10px;
      padding: 1.5px;
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      -webkit-mask: linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      opacity: 0;
      transition: opacity 0.2s;
      z-index: -1;
    }

    .checkbox-wrapper:hover {
      border-color: #cbd5e1;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      transform: translateY(-0.5px);
    }

    .checkbox-wrapper.checked {
      background: linear-gradient(135deg, #f5f7ff 0%, #eff2ff 100%);
      border-color: #c7d2fe;
      box-shadow: 0 2px 12px rgba(99, 102, 241, 0.12);
    }

    .checkbox-wrapper.checked::before {
      opacity: 1;
    }

    .checkbox-wrapper.error {
      border-color: #fca5a5;
      background: #fef8f8;
      box-shadow: 0 2px 8px rgba(239, 68, 68, 0.08);
    }

    .checkbox-wrapper:active {
      transform: scale(0.99);
    }

    /* Modern iOS-style checkbox */
    input[type='checkbox'] {
      position: relative;
      width: 20px;
      height: 20px;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      border: 2px solid #d1d5db;
      border-radius: 5px;
      background: #ffffff;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      flex-shrink: 0;
    }

    input[type='checkbox']:hover {
      border-color: #9ca3af;
      transform: scale(1.05);
    }

    input[type='checkbox']:checked {
      background: linear-gradient(135deg, #6366f1, #4f46e5);
      border-color: #4f46e5;
      box-shadow: 0 2px 8px rgba(79, 70, 229, 0.3);
    }

    input[type='checkbox']:checked::after {
      content: '';
      position: absolute;
      left: 6px;
      top: 2px;
      width: 4px;
      height: 9px;
      border: solid white;
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
      animation: checkDraw 0.25s cubic-bezier(0.65, 0, 0.35, 1) forwards;
    }

    input[type='checkbox']:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
    }

    input[type='checkbox']:checked:focus-visible {
      box-shadow: 0 2px 8px rgba(79, 70, 229, 0.3),
        0 0 0 3px rgba(99, 102, 241, 0.2);
    }

    @keyframes checkDraw {
      0% {
        height: 0;
        width: 0;
        opacity: 0;
      }
      40% {
        height: 0;
        width: 4px;
        opacity: 1;
      }
      100% {
        height: 9px;
        width: 4px;
        opacity: 1;
      }
    }

    label {
      font-weight: 500;
      color: #374151;
      font-size: 14px;
      cursor: pointer;
      user-select: none;
      flex: 1;
      display: flex;
      align-items: center;
      gap: 6px;
      line-height: 1.5;
      transition: color 0.2s;
    }

    .checkbox-wrapper.checked label {
      color: #4338ca;
    }

    .required {
      color: #ef4444;
      font-weight: 700;
      margin-left: 2px;
    }

    .error-message {
      color: #dc2626;
      font-size: 12px;
      margin-top: 8px;
      padding-left: 36px;
      display: flex;
      align-items: flex-start;
      gap: 6px;
      font-weight: 500;
      line-height: 1.4;
      animation: errorSlide 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }

    .error-message::before {
      content: '⚠';
      font-size: 14px;
      flex-shrink: 0;
    }

    @keyframes errorSlide {
      from {
        opacity: 0;
        transform: translateY(-4px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      * {
        animation-duration: 0.01ms !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;

  private _handleChange(e: Event) {
    const input = e.target as HTMLInputElement;
    this.value = input.checked;
    this.dispatchEvent(
      new CustomEvent('field-change', {
        detail: { name: this.name, value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }

  private _handleBlur() {
    this.dispatchEvent(
      new CustomEvent('field-blur', {
        detail: { name: this.name },
        bubbles: true,
        composed: true,
      })
    );
  }

  render() {
    const hasError = this.error && this.touched;
    const wrapperClasses = [
      'checkbox-wrapper',
      this.value ? 'checked' : '',
      hasError ? 'error' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return html`
      <div class="field-group">
        <div class="${wrapperClasses}" @click=${this._handleWrapperClick}>
          <input
            type="checkbox"
            id=${this.name}
            .checked=${this.value}
            @change=${this._handleChange}
            @blur=${this._handleBlur}
            @click=${(e: Event) => e.stopPropagation()} />
          <label for=${this.name}>
            ${this.label}
            ${this.required ? html`<span class="required">*</span>` : ''}
          </label>
        </div>
        ${hasError
          ? html` <div class="error-message">${this.error}</div> `
          : ''}
      </div>
    `;
  }

  private _handleWrapperClick() {
    // Toggle checkbox when clicking anywhere on the wrapper
    this.value = !this.value;
    this.dispatchEvent(
      new CustomEvent('field-change', {
        detail: { name: this.name, value: this.value },
        bubbles: true,
        composed: true,
      })
    );
  }
}
